import * as React from 'react';

import { composeRefs } from './composeRefs';
import { useIsomorphicLayoutEffect } from './useIsomorphicLayoutEffect';

export interface PresenceProps {
  present: boolean;
  /**
   * When true, the first paint after `present` becomes true uses `data-state="closed"` then flips to
   * `"open"` on the next frame so CSS transitions can run (drawer, dialogs). Leave false for
   * components where a brief closed state would flash (e.g. collapsible content).
   */
  animateEnter?: boolean;
  /** Fires after the exit transition/animation completes and the node is about to unmount. */
  onExitComplete?: () => void;
  children: React.ReactElement | ((props: { present: boolean }) => React.ReactElement);
}

function resolveChild(
  children: PresenceProps['children'],
  present: boolean,
): React.ReactElement {
  return typeof children === 'function' ? children({ present }) : children;
}

function parseMaxDurationMs(cssValue: string): number {
  return Math.max(
    0,
    ...cssValue.split(',').map((part) => {
      const v = part.trim();
      if (!v) return 0;
      const num = Number.parseFloat(v);
      if (Number.isNaN(num)) return 0;
      if (v.endsWith('ms')) return num;
      return num * 1000;
    }),
  );
}

export const Presence: React.FC<PresenceProps> = ({ present, animateEnter = false, onExitComplete, children }) => {
  const [rendered, setRendered] = React.useState(present);
  const [enterPhase, setEnterPhase] = React.useState<'open' | 'closed'>(() =>
    present && animateEnter ? 'closed' : present ? 'open' : 'closed'
  );
  const hostRef = React.useRef<HTMLElement | null>(null);
  const mountedRef = React.useRef(false);
  const prevPresentRef = React.useRef(present);
  const onExitCompleteRef = React.useRef(onExitComplete);
  onExitCompleteRef.current = onExitComplete;

  useIsomorphicLayoutEffect(() => {
    const isInitialMount = !mountedRef.current;
    mountedRef.current = true;
    const prev = prevPresentRef.current;
    prevPresentRef.current = present;

    if (present) {
      setRendered(true);
      if (!animateEnter) {
        setEnterPhase('open');
        return undefined;
      }
      if (isInitialMount || !prev) {
        setEnterPhase('closed');
        const id = requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setEnterPhase('open');
          });
        });
        return () => cancelAnimationFrame(id);
      }
      setEnterPhase('open');
      return undefined;
    }

    setEnterPhase('closed');
    return undefined;
  }, [present, animateEnter]);

  useIsomorphicLayoutEffect(() => {
    if (present || !rendered) return undefined;

    if (typeof window === 'undefined') {
      setRendered(false);
      return undefined;
    }

    const host = hostRef.current;
    if (!host) {
      setRendered(false);
      return undefined;
    }

    let cancelled = false;
    let timeoutId: number | undefined;

    const finish = () => {
      if (!cancelled) {
        setRendered(false);
        onExitCompleteRef.current?.();
      }
    };

    const onEnd = (event: AnimationEvent | TransitionEvent) => {
      if (event.target !== host) return;
      finish();
    };

    host.addEventListener('animationend', onEnd);
    host.addEventListener('transitionend', onEnd);

    const computed = window.getComputedStyle(host);
    const animDuration = parseMaxDurationMs(computed.animationDuration);
    const transDuration = parseMaxDurationMs(computed.transitionDuration);
    const hasRunningAnimation = animDuration > 0 || transDuration > 0;
    const maxDurationMs = Math.max(animDuration, transDuration);

    timeoutId = window.setTimeout(finish, hasRunningAnimation ? maxDurationMs + 50 : 0);

    return () => {
      cancelled = true;
      if (timeoutId != null) window.clearTimeout(timeoutId);
      host.removeEventListener('animationend', onEnd);
      host.removeEventListener('transitionend', onEnd);
    };
  }, [present, rendered]);

  if (!rendered) return null;

  const element = resolveChild(children, present);
  const state: 'open' | 'closed' = present ? (animateEnter ? enterPhase : 'open') : 'closed';
  const originalRef = (element as React.ReactElement & { ref?: React.Ref<HTMLElement | null> }).ref;

  return React.cloneElement(element, {
    ref: composeRefs(originalRef, hostRef),
    'data-state': state,
  } as typeof element.props);
};

Presence.displayName = 'Presence';
